import { ArrowUpRight, Activity } from "lucide-react";
import {
  FileStack,
  FileText,
  Ticket,
  HeartPulse,
  ShoppingBag,
  Trophy,
  Dumbbell,
  Code,
  Globe,
  Smartphone,
  Briefcase,
  Camera,
  Music,
  Book,
  Star,
  Zap,
  Mail,
  Image,
  Video,
  ShoppingCart,
  Wallet,
  Calendar,
  Database,
  Cloud,
  Box,
} from "lucide-react";
import { trackClick } from "@/lib/api";

const ICONS = {
  FileStack,
  FileText,
  Ticket,
  HeartPulse,
  ShoppingBag,
  Trophy,
  Dumbbell,
  Code,
  Globe,
  Smartphone,
  Briefcase,
  Camera,
  Music,
  Book,
  Star,
  Zap,
  Mail,
  Image,
  Video,
  ShoppingCart,
  Wallet,
  Calendar,
  Database,
  Cloud,
  Box,
};

const ACCENT_MAP = {
  green: {
    glow: "hover:glow-green",
    text: "text-[hsl(var(--accent-green))]",
    bg: "bg-[hsl(var(--accent-green)/0.1)]",
    border: "border-[hsl(var(--accent-green)/0.3)]",
    hoverBorder: "hover:border-[hsl(var(--accent-green)/0.6)]",
  },
  cyan: {
    glow: "hover:glow-cyan",
    text: "text-[hsl(var(--accent-cyan))]",
    bg: "bg-[hsl(var(--accent-cyan)/0.1)]",
    border: "border-[hsl(var(--accent-cyan)/0.3)]",
    hoverBorder: "hover:border-[hsl(var(--accent-cyan)/0.6)]",
  },
  magenta: {
    glow: "hover:glow-magenta",
    text: "text-[hsl(var(--accent-magenta))]",
    bg: "bg-[hsl(var(--accent-magenta)/0.1)]",
    border: "border-[hsl(var(--accent-magenta)/0.3)]",
    hoverBorder: "hover:border-[hsl(var(--accent-magenta)/0.6)]",
  },
};

export const AppCard = ({ app, onClicked }) => {
  const Icon = ICONS[app.icon] || Box;
  const accent = ACCENT_MAP[app.accent] || ACCENT_MAP.cyan;

  const handleOpen = async (e) => {
    // Don't block the link; track in background
    try {
      const res = await trackClick(app.slug);
      onClicked?.(app.slug, res.clicks);
    } catch (err) {
      // silent fail; never break the user's outbound click
    }
  };

  return (
    <a
      data-testid={`app-card-${app.slug}`}
      href={app.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleOpen}
      className={[
        "group relative flex flex-col glass hover-lift p-7 md:p-8 min-h-[260px]",
        accent.hoverBorder,
        accent.glow,
      ].join(" ")}
    >
      {/* Top-row */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div
          className={[
            "w-12 h-12 grid place-items-center border",
            accent.bg,
            accent.border,
            accent.text,
          ].join(" ")}
        >
          <Icon size={20} strokeWidth={1.75} />
        </div>

        <div className="flex items-center gap-2">
          <span
            data-testid={`app-category-${app.slug}`}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground border border-border px-2 py-1"
          >
            {app.category}
          </span>
          <ArrowUpRight
            size={18}
            className="opacity-50 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
          />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col">
        <h3 className="font-display font-bold leading-tight mb-2 text-2xl md:text-3xl">
          {app.name}
        </h3>
        <p className="text-muted-foreground mb-6 text-sm md:text-base">
          {app.tagline}
        </p>

        <div className="mt-auto flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
            <Activity size={12} className={accent.text} />
            <span
              data-testid={`app-clicks-${app.slug}`}
              className="tabular-nums"
            >
              {app.clicks.toLocaleString("es-AR")} visitas
            </span>
          </div>
          <span
            data-testid={`app-link-${app.slug}`}
            className={[
              "font-mono text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-1",
              accent.text,
            ].join(" ")}
          >
            open
            <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </a>
  );
};

export default AppCard;
